import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef
} from 'react';
import useTranslations from '../../../hooks/useTranslations';
import { FetchCallbacksContext } from '../../../internal-components/FetchCallbacks';
import { FormContext } from '../FormContext';
import FieldView from '../view/FieldView';

const arePropsEqual = (prevProps, nextProps) => {
  return JSON.stringify(prevProps) === JSON.stringify(nextProps);
};

const FieldContext = createContext({});
/**
 *
 * @param {Object} field - Field object as stored in form object
 * @param {String} initialValue - The initial value for this field
 */
const FieldEngine = React.memo(
  ({
    field,
    initialValue = '',
    initialLookupData,
    isAdmin,
    variant,
    onChangeValue = () => {}
  }) => {
    const t = useTranslations();
    const { openSnackbar } = useContext(FetchCallbacksContext);
    const {
      formId,
      isReadOnlyFieldsEditable,
      onFormChange,
      onToggleSectionCollapse,
      isFormReadOnly,
      endpointType,
      viewOnlyAdminMode
    } = useContext(FormContext);
    const [value, setValue] = useState(initialValue);
    const firstRender = useRef(true);
    useEffect(() => {
      if (firstRender.current) {
        firstRender.current = false;
        return;
      }
      onFormChange(null, null, null, field?.internalId);
      onChangeValue(value);
    }, [value]);

    const classes = `EUF-base${field.admin ? ' EUF-admin' : ''}${
      viewOnlyAdminMode ? ' EUF-view-only-admin' : ''
    }`;

    const onClick = useCallback(() => {
      if (!isReadOnlyFieldsEditable && field.readOnly && !isFormReadOnly) {
        openSnackbar('warning', t('snackbars.field.read_only'));
      }
    }, [isReadOnlyFieldsEditable, field, isFormReadOnly]);

    const onFocus = useCallback(() => {
      try {
        const fieldNode = document.querySelector(
          `[field-id="${field.internalId}"]`
        );
        if (!fieldNode) {
          return;
        }
        const errorP = fieldNode.querySelector('[data-error]');
        if (!errorP) {
          return;
        }
        fieldNode.classList.remove('EUF-error');
        errorP.textContent = '';
      } catch (e) {
        return;
      }
    }, [field]);

    const contextProps = { formId, value, setValue };

    const isInstructionalText = field.uiType === 'instructional_text';
    const isSectionBreak = field.uiType === 'section_break';

    const viewProps = {
      ...field,
      classes,
      initialLookupData,
      value,
      setValue,
      onClick,
      onFocus,
      onFormChange,
      onToggleSectionCollapse,
      isUsingDescriptionLogic: typeof field.description === 'object',
      ignoreDescription: isInstructionalText || isSectionBreak,
      endpointType,
      variant
    };

    return (
      <FieldContext.Provider value={contextProps}>
        <FieldView {...viewProps} />
      </FieldContext.Provider>
    );
  },
  arePropsEqual
);

export default FieldEngine;
